import { useEffect, useState } from "react";
import { applyAppearance } from "./appearance/apply";
import { paletteMode, resolvePaletteForSkin } from "./appearance/registry";
import type { AppearanceState, SkinEntry } from "./appearance/types";
import { ConvertScreen } from "./components/ConvertScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NavBar } from "./components/NavBar";
import { ThemesScreen } from "./components/ThemesScreen";
import { typedAmount, type AmountState } from "./convert/baseSwap";
import { FIXTURE_SNAPSHOT } from "./rates/fixture";
import { useRoute } from "./router";
import type { SessionState } from "./session/schema";
import { installSessionFlushHandlers, loadSession, saveSession } from "./session/storage";

/**
 * Read synchronously at module init, BEFORE React mounts, and used as the
 * initial state below — never defaults corrected later in a useEffect.
 */
const initialSession: SessionState = loadSession({ language: navigator.language });

/**
 * Applying a skin writes the three attributes the document actually carries.
 * The skin itself is never stored — form + layout are, exactly as before, so
 * the persisted shape and the CSS architecture are untouched by the skin model.
 */
function appearanceFor(skin: SkinEntry, paletteId: string): AppearanceState {
  const palette = resolvePaletteForSkin(paletteId, skin.id);
  return {
    palette,
    form: skin.form,
    layout: skin.layout,
    colorScheme: paletteMode(palette),
  };
}

export default function App() {
  const [session, setSession] = useState<SessionState>(initialSession);
  const [amount, setAmount] = useState<AmountState>(typedAmount(initialSession.amount));
  const [route, navigate] = useRoute();

  const { appearance, locale } = session;

  useEffect(() => {
    installSessionFlushHandlers();
  }, []);

  useEffect(() => {
    applyAppearance(appearance);
  }, [appearance]);

  // Language and direction belong on <html> so the whole document inherits them.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  // Persist the raw typed string, per the session contract.
  useEffect(() => {
    saveSession({ ...session, amount: amount.input });
  }, [session, amount.input]);

  const applySkin = (skin: SkinEntry, paletteId: string) => {
    setSession((prev) => ({ ...prev, appearance: appearanceFor(skin, paletteId) }));
  };

  return (
    <div className="app">
      {/*
       * Inside .app, not around it, so a crash keeps the page's own background
       * and chrome instead of dropping to a bare white document — the exact
       * failure this replaces.
       */}
      <ErrorBoundary locale={locale}>
      <div className="app__main">
        {route === "convert" ? (
          <ConvertScreen
            locale={locale}
            snapshot={FIXTURE_SNAPSHOT}
            base={session.base}
            codes={session.quotes}
            amount={amount}
            onAmountChange={setAmount}
            onBaseChange={(base, nextAmount) => {
              setSession((prev) => ({ ...prev, base }));
              setAmount(nextAmount);
            }}
            onCodesChange={(quotes) => setSession((prev) => ({ ...prev, quotes }))}
          />
        ) : (
          <ThemesScreen locale={locale} appearance={appearance} onApply={applySkin} />
        )}
      </div>
      </ErrorBoundary>

      <NavBar route={route} locale={locale} onNavigate={navigate} />
    </div>
  );
}
